<script setup>

import {ref, onMounted} from "vue";


const props = defineProps({
    personalized: {
        type: Boolean,
        required: true,
    },
    subject: {
        type: String,
        required: true
    },
    body: {
        type: String,
        required: true
    },
    lead_id: {
        type: [String, Number],
        required: true
    },
    domain: {
        type: String,
        required: true
    }
})

const editingEmail = ref(false)
const _subject = ref(props.subject)
const _body = ref(props.body)
const constructiveFeedback = ref('')

const personalizationState = ref('')
const poller = ref(null)

const polling = ref(false)

const showFeedback = ref(false)

const initPersonalization = async () => {
    polling.value = true

    if (personalizationState.value === 'waiting') {
        return
    }

    personalizationState.value = 'waiting'

    await axios.post(route('assistant.personalizeEmail'), {
        lead_id: props.lead_id,
        message: props.body,
        subject: props.subject,
        domain: props.domain
    })

    poller.value = setInterval(async () => {
        const response = await axios.get(route('assistant.personalizedEmail', {
            lead_id: props.lead_id,
        }))

        if (response.data.success) {
            personalizationState.value = 'success'
            clearInterval(poller.value)
            poller.value = null
            _subject.value = response.data.subject
            _body.value = response.data.body
            constructiveFeedback.value = response.data.constructive_feedback

            polling.value = false
        } else if (response.data.status === 'error') {
            clearInterval(poller.value)
            poller.value = null
            console.error('Error in personalization:', response.data.error)
        }
    }, 5000)
}

const getEmail = async () => {
    polling.value = true
    const response = await axios.get(route('assistant.getEmail', {
        lead_id: props.lead_id,
    }))

    if (response.data.personalized) {
        _subject.value = response.data.subject
        _body.value = response.data.body
        constructiveFeedback.value = response.data.constructive_feedback
    }

    polling.value = false
}

onMounted(() => {
    getEmail()
})
</script>

<template>
<div>
    <div class="flex justify-between -mx-4 md:-mx-6 border-b px-4 md:px-6 pb-2">
        <h5>{{ props.personalized ? 'Your personalized email' : 'Your base email' }}</h5>
        <div class="flex gap-2">
            <button-primary
                v-if="! props.personalized && ! editingMessage && !polling && !constructiveFeedback"
                :state="personalizationState"
                size="sm"
                @click="initPersonalization"
            >
                Personalize
            </button-primary>
        </div>
    </div>

    <div
        class="py-4 -mx-4 md:-mx-6 md:px-6"
        v-if="!polling"
    >
        <div v-if="constructiveFeedback">
            <div
                v-if="showFeedback"
                class="text-sm italic mb-4"
            >
                <div v-html="constructiveFeedback.replace(/\n/g, '<br>').replace(/\*\*/g, '')"></div>
            </div>
            <button-base
                size="sm"
                class="mb-4"
                @click="showFeedback = !showFeedback"
            >
                {{ showFeedback ? 'Hide constructive feedback' : 'Show constructive feedback' }}
            </button-base>
        </div>

        <div class="flex items-center justify-between space-x-4">
            <span class="font-bold">Subject</span>
            <container-with-tooltip
                alignment="center"
                tooltip-class="mb-1 bg-white text-xs px-1 py-0.5 rounded shadow-md text-xss"
            >
                <template #tooltip>
                    Copy to clipboard
                </template>

                <copyable-text
                    :show-url="false"
                    :copyable-text="_subject.replace(/<[^>]*>/g, '\n').replace(/\n{2,}/g, '\n')"
                />
            </container-with-tooltip>
        </div>
        <input-text
            v-if="editingEmail"
            v-model:content="_subject"
        />
        <div v-else class="select-all">
            {{ _subject }}
        </div>

        <hr class="mt-4 mb-4">

        <div class="flex items-center justify-between space-x-4">
            <span class="font-bold">Content</span>
            <container-with-tooltip
                alignment="center"
                tooltip-class="mb-1 bg-white text-xs px-1 py-0.5 rounded shadow-md text-xss"
            >
                <template #tooltip>
                    Copy to clipboard
                </template>

                <copyable-text
                    :show-url="false"
                    :copyable-text="_body.replace(/<\/?p[^>]*>/gi, '\n\n').replace(/<[^>]*>/g, '').replace(/\n{2,}/g, '\n\n').replace(/&nbsp;/g, ' ')"
                />
            </container-with-tooltip>
        </div>

        <input-editor
            v-if="editingEmail"
            v-model:content="_body"
        />
        <div
            v-else
        >
            <div
                v-html="_body.replace(/\n/g, '<br>')"
                class="select-all"
            >
            </div>
            <p class="text-xs text-gray-400 mt-4 border-t pt-2">
                Having issues when using the Copy to clipboard feature? <br>Click the text to select it, then manually copy it. This solves most issues with email clients experiencing problems with formatting.
            </p>
        </div>
    </div>
    <div v-else>
        <div class="flex items-center justify-center">
            <mf-spinner-medium class="w-24 h-12"></mf-spinner-medium>
        </div>
    </div>
</div>
</template>

<style scoped>

</style>
